import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  pipelineStageSchema,
  type PipelineStage,
  type PortalRegistrationDto,
} from '@wally-reseller/shared';
import { Button } from '@wally-reseller/ui/button';
import { Input, Label } from '@wally-reseller/ui/input';
import { Select } from '@wally-reseller/ui/select';
import { useState } from 'react';
import { api } from '@/lib/api';
import { useFormatDate, useT } from '@/lib/i18n';

/** A partner that sells itself sets where an accepted lead stands (and the contract date). */
export function StageReport({ registration: r }: { registration: PortalRegistrationDto }) {
  const t = useT();
  const fmt = useFormatDate();
  const queryClient = useQueryClient();
  const current = (r.stageReport?.stage ?? 'in_progress') as PipelineStage;
  const [stage, setStage] = useState<PipelineStage>(current);
  const [signedOn, setSignedOn] = useState(r.stageReport?.contractSignedOn ?? '');
  const save = useMutation({
    mutationFn: () =>
      api.reportStage(r.id, {
        stage,
        ...(stage === 'customer' && signedOn ? { contractSignedOn: signedOn } : {}),
      }),
    onSuccess: (updated) => {
      queryClient.setQueryData(['registrations', r.id], updated);
      void queryClient.invalidateQueries({ queryKey: ['registrations'], exact: true });
      void queryClient.invalidateQueries({ queryKey: ['registration-counts'] });
    },
  });
  // A first report is always worth saving, even "in progress": until then HubSpot's stage shows.
  const changed =
    !r.stageReport ||
    stage !== current ||
    (stage === 'customer' && signedOn !== (r.stageReport?.contractSignedOn ?? ''));
  return (
    <form
      className="space-y-3 rounded-lg border border-border bg-background p-4 text-sm"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      <div>
        <h2 className="font-medium">{t('portal.lead.stage.title')}</h2>
        <p className="text-xs text-muted-foreground">{t('portal.lead.stage.hint')}</p>
      </div>
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1">
          <Label>{t('portal.lead.stage.label')}</Label>
          <Select
            value={stage}
            onValueChange={setStage}
            options={pipelineStageSchema.options.map((s) => ({
              value: s,
              label: t(`portal.status.${s}`),
            }))}
            className="w-56"
          />
        </div>
        {stage === 'customer' && (
          <div className="space-y-1">
            <Label htmlFor="signed-on">{t('portal.lead.stage.signedOn')}</Label>
            <Input
              id="signed-on"
              type="date"
              className="h-8 w-44"
              // Today in Brussels, as the server checks it.
              max={new Intl.DateTimeFormat('en-CA', {
                timeZone: 'Europe/Brussels',
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
              }).format(new Date())}
              value={signedOn}
              onChange={(e) => setSignedOn(e.target.value)}
            />
          </div>
        )}
        <Button type="submit" size="sm" disabled={!changed || save.isPending}>
          {t('portal.lead.stage.save')}
        </Button>
      </div>
      {save.isError && <p className="text-danger">{t('portal.error.generic')}</p>}
      {r.stageReport && (
        <p className="text-xs text-muted-foreground">
          {t('portal.lead.stage.updated', { date: fmt(r.stageReport.at), name: r.stageReport.by })}
        </p>
      )}
    </form>
  );
}
